'use client';

import { c as _c } from "react/compiler-runtime";
import { type GetLeaderboardRow, useAuth, useGetLeaderboard, usePrefetchGetUser } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { userIdentityKey } from '@/components/profile/user-identity-key';
import { parseAvatarPreferences } from '@/components/social/avatar-preferences';
import { LeaderboardEmptyState } from '@/components/social/leaderboard/leaderboard-empty';
import { LeaderboardSkeleton } from '@/components/social/leaderboard/leaderboard-skeleton';
import { TabContent } from '@/components/social/leaderboard/tab-content';
import { useAnchorScroll } from '@/components/social/leaderboard/use-anchor-scroll';
import { mapLeaderboardEntries, readFiniteNumber, readFiniteNumericString } from '@/components/social/leaderboard/use-committed-leaderboard-entries';
import { SocialFeedList } from '@/components/social/shared/social-feed-list';
import type { BoosterEntry, BoosterSort } from '@/components/social/types';
import { DataTable } from '@/components/ui/data-table';
import { Panel } from '@/components/ui/panel';
import { useLiveListData } from '@/hooks/sdk/use-live-list-data';
import { useAppRouter } from '@/lib/network/use-app-router';
import { LB_BOOSTERS_FIELDS, PORTFOLIO_USER_PROFILE_FIELDS } from '@/lib/sdk/projections';
import { SOCIAL_LEADERBOARD_LIST_PANEL_CLASS } from '@/lib/ui/layout';

// ---------------------------------------------------------------------------
// Boosters tab — consumes the universal DataTable primitive (spec 093).
// Three sortable columns: Boost Volume (default), Boosts, Markets.
// ---------------------------------------------------------------------------

interface BoostersTabProps {
  initialSort?: BoosterSort;
  onSortChange?: (sort: BoosterSort) => void;
  anchorUserId?: string | undefined;
  pulseTick?: number | undefined;
  /** Time window forwarded to the leaderboard hook (`1d`/`1w`/`1m`/`1y`/`lifetime`). */
  timeWindow?: string;
}
function rankForActiveSort(entry: BoosterEntry, sortBy: BoosterSort): number {
  switch (sortBy) {
    case 'amount':
      return entry.amountRank;
    case 'boosts':
      return entry.boostsRank;
    case 'marketsBoosted':
      return entry.marketsBoostedRank;
  }
}
function mapEntry(item: GetLeaderboardRow, sortBy: BoosterSort): BoosterEntry | null {
  const userField = (item.user ?? {}) as Record<string, unknown>;
  const providersRaw = Array.isArray(userField.connectedProviders) ? userField.connectedProviders as Array<Record<string, unknown>> : [];
  const totalAmountBoosted = readFiniteNumericString(item.totalAmountBoosted);
  const numBoosts = readFiniteNumber(item.numBoosts);
  const marketsBoosted = item.marketsBoosted === null ? 0 : readFiniteNumber(item.marketsBoosted);
  const activeRank = readFiniteNumber(sortBy === 'amount' ? item.amountRank : sortBy === 'boosts' ? item.boostsRank : item.marketsBoostedRank);
  const amountRank = item.amountRank === null ? activeRank : readFiniteNumber(item.amountRank);
  const boostsRank = item.boostsRank === null ? activeRank : readFiniteNumber(item.boostsRank);
  const marketsBoostedRank = item.marketsBoostedRank === null ? activeRank : readFiniteNumber(item.marketsBoostedRank);
  if (totalAmountBoosted === null || numBoosts === null || marketsBoosted === null || activeRank === null || amountRank === null || boostsRank === null || marketsBoostedRank === null) {
    return null;
  }
  return {
    user: {
      userId: typeof userField.userId === 'string' ? userField.userId : null,
      walletAddress: typeof userField.walletAddress === 'string' ? userField.walletAddress : '',
      displayName: typeof userField.displayName === 'string' ? userField.displayName : null,
      handle: typeof userField.handle === 'string' ? userField.handle : null,
      avatarUrl: typeof userField.avatarUrl === 'string' ? userField.avatarUrl : null,
      avatarNsfw: userField.avatarNsfw === true,
      avatarPreferences: parseAvatarPreferences(userField.avatarPreferences),
      connectedProviders: providersRaw.filter((p): p is {
        provider: 'x' | 'google' | 'apple';
      } & Record<string, unknown> => p.provider === 'x' || p.provider === 'google' || p.provider === 'apple').map(p => ({
        provider: p.provider,
        username: typeof p.username === 'string' ? p.username : '',
        displayName: typeof p.displayName === 'string' ? p.displayName : null
      }))
    },
    totalAmountBoosted,
    numBoosts,
    marketsBoosted,
    amountRank,
    boostsRank,
    marketsBoostedRank
  };
}
export function BoostersTab(t0) {
  const $ = _c(66);
  const {
    initialSort,
    onSortChange,
    anchorUserId,
    pulseTick,
    timeWindow
  } = t0;
  const tBoosters = useTranslations("social.boosters");
  const tLb = useTranslations("social.leaderboard");
  const {
    user
  } = useAuth();
  const router = useAppRouter();
  const prefetchUser = usePrefetchGetUser();
  const userId = user?.id ?? null;
  const [sortBy, setSortBy] = useState(initialSort ?? "amount");
  const sortOrder = "desc" as const;
  const leaderboardWindow = timeWindow ?? "lifetime";
  let t1;
  if ($[0] !== onSortChange) {
    t1 = function handleSortChange(next) {
      setSortBy(next);
      if (onSortChange) {
        onSortChange(next);
      }
    };
    $[0] = onSortChange;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleSortChange = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [...LB_BOOSTERS_FIELDS];
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== leaderboardWindow || $[4] !== sortBy) {
    t3 = {
      kind: "boosters",
      sortBy,
      sortOrder: "desc",
      window: leaderboardWindow,
      fields: t2
    };
    $[3] = leaderboardWindow;
    $[4] = sortBy;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const leaderboardQuery = useGetLeaderboard(t3);
  const leaderboardStatus = leaderboardQuery.status;
  const leaderboardData = useLiveListData(leaderboardQuery.queryKey);
  const isResolved = leaderboardStatus === "success" && leaderboardQuery.isFetched && !leaderboardQuery.isPlaceholderData;
  let isInitialLoad;
  let rawEntries;
  let t4;
  if ($[6] !== anchorUserId || $[7] !== isResolved || $[8] !== leaderboardData || $[9] !== sortBy) {
    rawEntries = mapLeaderboardEntries(leaderboardData, isResolved, mapEntry, sortBy);
    isInitialLoad = !isResolved && rawEntries.length === 0;
    t4 = anchorUserId !== undefined && rawEntries.some(entry => entry.user.userId === anchorUserId);
    $[6] = anchorUserId;
    $[7] = isResolved;
    $[8] = leaderboardData;
    $[9] = sortBy;
    $[10] = isInitialLoad;
    $[11] = rawEntries;
    $[12] = t4;
  } else {
    isInitialLoad = $[10];
    rawEntries = $[11];
    t4 = $[12];
  }
  const isAnchorLoaded = t4;
  const t5 = leaderboardQuery.hasNextPage === true;
  const t6 = leaderboardQuery.isFetchingNextPage === true;
  let t7;
  if ($[13] !== anchorUserId || $[14] !== isAnchorLoaded || $[15] !== leaderboardQuery.fetchNextPage || $[16] !== pulseTick || $[17] !== t5 || $[18] !== t6) {
    t7 = {
      pulseTick,
      anchorUserId,
      isAnchorLoaded,
      hasNextPage: t5,
      isFetchingNextPage: t6,
      fetchNextPage: leaderboardQuery.fetchNextPage
    };
    $[13] = anchorUserId;
    $[14] = isAnchorLoaded;
    $[15] = leaderboardQuery.fetchNextPage;
    $[16] = pulseTick;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const scrollTick = useAnchorScroll(t7);
  if (isInitialLoad) {
    let t8;
    if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <TabContent><Panel surface="solid" inheritBackground={true} className={SOCIAL_LEADERBOARD_LIST_PANEL_CLASS} data-testid="social-leaderboard-panel"><LeaderboardSkeleton columnCount={3} /></Panel></TabContent>;
      $[20] = t8;
    } else {
      t8 = $[20];
    }
    return t8;
  }
  if (rawEntries.length === 0) {
    let t8;
    if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <TabContent><Panel surface="solid" inheritBackground={true} className={SOCIAL_LEADERBOARD_LIST_PANEL_CLASS} data-testid="social-leaderboard-panel"><LeaderboardEmptyState tab="boosters" /></Panel></TabContent>;
      $[21] = t8;
    } else {
      t8 = $[21];
    }
    return t8;
  }
  const t8 = leaderboardQuery.hasNextPage === true;
  const t9 = leaderboardQuery.isFetchingNextPage === true;
  const t10 = leaderboardQuery.fetchNextPage;
  const t11 = leaderboardQuery.isError;
  let t12;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <DataTable.RankColumn />;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== tLb) {
    t13 = tLb("trader");
    $[23] = tLb;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== t13) {
    t14 = <DataTable.IdentityColumn header={t13} />;
    $[25] = t13;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== tBoosters) {
    t15 = tBoosters("column_boost_volume");
    $[27] = tBoosters;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== t15) {
    t16 = <DataTable.SortableColumn sortKey="amount" header={t15} align="right" className="w-[28%] whitespace-nowrap" />;
    $[29] = t15;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== tBoosters) {
    t17 = tBoosters("column_boosts");
    $[31] = tBoosters;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== t17) {
    t18 = <DataTable.SortableColumn sortKey="boosts" header={t17} align="right" className="w-[18%] whitespace-nowrap" />;
    $[33] = t17;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] !== tBoosters) {
    t19 = tBoosters("column_markets");
    $[35] = tBoosters;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  let t20;
  if ($[37] !== tBoosters) {
    t20 = tBoosters("column_markets_tooltip");
    $[37] = tBoosters;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== t19 || $[40] !== t20) {
    t21 = <DataTable.SortableColumn sortKey="marketsBoosted" header={t19} headerTooltip={t20} align="right" className="w-[18%] whitespace-nowrap" />;
    $[39] = t19;
    $[40] = t20;
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  let t22;
  if ($[42] !== t14 || $[43] !== t16 || $[44] !== t18 || $[45] !== t21) {
    t22 = <DataTable.Header>{t12}{t14}{t16}{t18}{t21}</DataTable.Header>;
    $[42] = t14;
    $[43] = t16;
    $[44] = t18;
    $[45] = t21;
    $[46] = t22;
  } else {
    t22 = $[46];
  }
  let t23;
  if ($[47] !== prefetchUser || $[48] !== router || $[49] !== sortBy || $[50] !== tLb || $[51] !== userId) {
    t23 = <DataTable.Body>{entry_0 => {
        const rank = rankForActiveSort(entry_0, sortBy);
        const entryUserId = entry_0.user.userId ?? "";
        const isUser = userId !== null && entry_0.user.userId === userId;
        const navigate = function navigate() {
          if (entryUserId.length === 0) {
            return;
          }
          router.push(`/portfolio/${entryUserId}`);
        };
        const prefetch = function prefetch() {
          if (entryUserId.length === 0) {
            return;
          }
          prefetchUser({
            identifier: entryUserId,
            fields: [...PORTFOLIO_USER_PROFILE_FIELDS]
          });
        };
        return <DataTable.Row key={userIdentityKey(entry_0.user, `anonymous-rank-${rank}`)} isCurrentUser={isUser} onClick={navigate} onMouseEnter={prefetch} onFocus={prefetch} onPointerDown={prefetch} ariaLabel={tLb("aria_view_profile")}><DataTable.RankCell value={rank} isCurrentUser={isUser} /><DataTable.IdentityCell user={entry_0.user} showYouBadge={isUser} /><DataTable.NumericCell value={Number(entry_0.totalAmountBoosted)} format="compact" /><DataTable.NumericCell value={entry_0.numBoosts} format="int" hideOnMobile={true} /><DataTable.NumericCell value={entry_0.marketsBoosted} format="int" hideOnMobile={true} /></DataTable.Row>;
      }}</DataTable.Body>;
    $[47] = prefetchUser;
    $[48] = router;
    $[49] = sortBy;
    $[50] = tLb;
    $[51] = userId;
    $[52] = t23;
  } else {
    t23 = $[52];
  }
  let t24;
  if ($[53] !== handleSortChange || $[54] !== rawEntries || $[55] !== scrollTick || $[56] !== sortBy || $[57] !== t22 || $[58] !== t23) {
    t24 = <DataTable panel="borderless" desktopRowChrome="feed" entries={rawEntries} sortBy={sortBy} sortOrder={sortOrder} onSortChange={handleSortChange} pulseTick={scrollTick}>{t22}{t23}</DataTable>;
    $[53] = handleSortChange;
    $[54] = rawEntries;
    $[55] = scrollTick;
    $[56] = sortBy;
    $[57] = t22;
    $[58] = t23;
    $[59] = t24;
  } else {
    t24 = $[59];
  }
  let t25;
  if ($[60] !== leaderboardQuery.fetchNextPage || $[61] !== leaderboardQuery.isError || $[62] !== t24 || $[63] !== t8 || $[64] !== t9) {
    t25 = <TabContent><Panel surface="solid" inheritBackground={true} className={SOCIAL_LEADERBOARD_LIST_PANEL_CLASS} data-testid="social-leaderboard-panel"><SocialFeedList hasNextPage={t8} isFetchingNextPage={t9} fetchNextPage={t10} isError={t11} data-testid="social-boosters-scroll">{t24}</SocialFeedList></Panel></TabContent>;
    $[60] = leaderboardQuery.fetchNextPage;
    $[61] = leaderboardQuery.isError;
    $[62] = t24;
    $[63] = t8;
    $[64] = t9;
    $[65] = t25;
  } else {
    t25 = $[65];
  }
  return t25;
}